import { compareStudentNumbers } from "./roster.js";
import type { DailyRecord, Student } from "./types.js";

export function classroomPerformance(
  classId: string,
  classRoster: Student[],
  records: DailyRecord[],
  startDate = "",
  endDate = "",
) {
  // The caller resolves shared rosters; records still belong to the selected subject class.
  const active = classRoster.filter((student) => student.active).sort(compareStudentNumbers);
  const latest = new Map<string, DailyRecord>();
  for (const record of records) {
    if (record.classId !== classId || (startDate && record.date < startDate) || (endDate && record.date > endDate))
      continue;
    const previous = latest.get(record.date);
    if (!previous || record.updatedAt > previous.updatedAt) latest.set(record.date, record);
  }
  const ordered = [...latest.values()].sort((a, b) => b.date.localeCompare(a.date));
  const students = active.map((student) => {
    const history = ordered.flatMap((record) => {
      const item = record.students[student.id];
      if (!item) return [];
      return [
        {
          date: record.date,
          answers: Number.isSafeInteger(item.answers) && item.answers >= 0 ? item.answers : 0,
          missingMaterials: item.missingMaterials === true,
          note: item.note,
        },
      ];
    });
    return {
      student,
      history,
      answers: history.reduce((sum, item) => sum + item.answers, 0),
      missingMaterials: history.filter((item) => item.missingMaterials).length,
      lastResponse: history.find((item) => item.answers > 0)?.date,
    };
  });
  const totalsByDate = new Map(
    ordered.map((record) => [record.date, { date: record.date, answers: 0, participants: 0, missingMaterials: 0 }]),
  );
  for (const student of students) {
    for (const item of student.history) {
      const day = totalsByDate.get(item.date);
      if (!day) continue;
      day.answers += item.answers;
      day.participants += item.answers > 0 ? 1 : 0;
      day.missingMaterials += item.missingMaterials ? 1 : 0;
    }
  }
  const days = [...totalsByDate.values()].reverse();
  return {
    students,
    days,
    answers: students.reduce((sum, student) => sum + student.answers, 0),
    missingMaterials: students.reduce((sum, student) => sum + student.missingMaterials, 0),
    participants: students.filter((student) => student.answers > 0).length,
    missingStudents: students.filter((student) => student.missingMaterials > 0).length,
  };
}
